/**
 * Special-week templates — Te Wiki, Mental Health Awareness Week, Pink Shirt Day.
 *
 * Staging/dev: admin can preview any template on/off.
 * Staging and production use the same NZ civil clock (`Pacific/Auckland`,
 * NZST / NZDT). Every special day/week reads `occurs_on` / `ends_on` from
 * the club calendar (catalog fallback). Cutover is 00:00 NZ the day before
 * that start date, then stays live through `ends_on`. Banner, chrome,
 * notices, and pulse share that predicate.
 */

import {
  eventHasPassed,
  eventIsActiveOn,
  isNzSchoolHolidaySlug,
  occurrencesForYear,
  occurrenceToDeckObservance,
  type CalendarOccurrence,
  type DeckObservance,
  type ObservanceKind,
} from './calendar';
import { addDaysIso, nzHourOfDay } from './time';

export const CAMPAIGN_TEMPLATE_SLUGS = [
  'te-wiki-o-te-reo-maori',
  'mental-health-awareness-week',
  'pink-shirt-day',
] as const;

export type CampaignTemplateSlug = (typeof CAMPAIGN_TEMPLATE_SLUGS)[number];

export const CAMPAIGN_TEMPLATE_LABELS: Record<CampaignTemplateSlug, string> = {
  'te-wiki-o-te-reo-maori': 'Te Wiki',
  'mental-health-awareness-week': 'MHAW',
  'pink-shirt-day': 'Pink Shirt Day',
};

export const PROMOTE_OVERNIGHT_HOUR_FROM = 0;
export const PROMOTE_OVERNIGHT_HOUR_TO = 5;

export type CampaignTemplateSpan = 'day' | 'week';

export interface CampaignTemplate {
  slug: CampaignTemplateSlug;
  shortLabel: string;
  title: string;
  occursOn: string | null;
  endsOn: string | null;
  span: CampaignTemplateSpan;
  description: string;
  sessionIdea: string;
  coachReminder: string;
  parentMessage: string;
  dressCode: string | null;
  themeColor: string;
}

/** Week if the catalog gives an end date after the start; otherwise a single day. */
export function campaignTemplateSpan(occursOn: string | null, endsOn: string | null): CampaignTemplateSpan {
  if (occursOn && endsOn && endsOn > occursOn) return 'week';
  return 'day';
}

export function isCampaignTemplateSlug(value: string | null | undefined): value is CampaignTemplateSlug {
  return !!value && (CAMPAIGN_TEMPLATE_SLUGS as readonly string[]).includes(value);
}

/** Preview toggles are staging/dev only. Production waits for the overnight window. */
export function campaignPreviewAllowed(environment: string | undefined | null): boolean {
  const env = (environment ?? '').toLowerCase();
  return env !== 'production' && env !== 'prod';
}

/** Ignore a leftover settings slug in production — preview is staging/dev only. */
export function effectiveCampaignPreviewSlug(
  stored: string | null | undefined,
  environment: string | undefined | null,
): CampaignTemplateSlug | null {
  if (!campaignPreviewAllowed(environment)) return null;
  return isCampaignTemplateSlug(stored) ? stored : null;
}

export function isOvernightPromoteWindow(nzHour: number): boolean {
  return nzHour >= PROMOTE_OVERNIGHT_HOUR_FROM && nzHour < PROMOTE_OVERNIGHT_HOUR_TO;
}

/** Night before the campaign start date (NZ calendar date). */
export function isPromoteNight(nzDate: string, occursOn: string | null | undefined): boolean {
  if (!occursOn) return false;
  return nzDate === addDaysIso(occursOn, -1);
}

/**
 * Overnight cron should write the calendar row:
 * - the night before `occursOn` (even if already adopted — refresh copy), or
 * - any later overnight hour while the event is live if it was never adopted
 *   (catch-up when promote night was missed, e.g. a Monday 00:00 prod ship).
 */
export function isCampaignPromoteDue(
  nzDate: string,
  occursOn: string | null | undefined,
  endsOn: string | null | undefined,
  opts: { alreadyAdopted?: boolean; nzHour?: number } = {},
): boolean {
  const nzHour = opts.nzHour ?? nzHourOfDay();
  if (!isOvernightPromoteWindow(nzHour)) return false;
  if (isPromoteNight(nzDate, occursOn)) return true;
  if (opts.alreadyAdopted) return false;
  return campaignHasCutOver(occursOn, endsOn, nzDate, nzHour);
}

/**
 * Public live window: from 00:00 NZ on the day before `occursOn` through `endsOn`.
 * Dates come from the club calendar row (or catalog if the club has not adopted it).
 * The cron applies templates during 00:00–05:00 NZ on that promote night,
 * and again on later overnight hours if the row was never adopted (catch-up).
 * After 05:00 the observance stays on. Hour is always NZ wall-clock.
 */
export function campaignHasCutOver(
  occursOn: string | null | undefined,
  endsOn: string | null | undefined,
  nzDate: string,
  nzHour: number = nzHourOfDay(),
): boolean {
  if (!occursOn) return false;
  if (eventIsActiveOn(occursOn, endsOn, nzDate)) return true;
  return isPromoteNight(nzDate, occursOn) && nzHour >= PROMOTE_OVERNIGHT_HOUR_FROM;
}

export function isSpecialObservanceKind(kind: string | null | undefined): boolean {
  return kind === 'awareness_day' || kind === 'club_theme' || kind === 'local_event';
}

export function isHolidayObservanceKind(kind: string | null | undefined): boolean {
  return kind === 'public_holiday' || kind === 'regional_holiday';
}

/** Look up catalog kind when the caller only has a slug. */
export function catalogKindForSlug(
  slug: string,
  year: number,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): ObservanceKind | null {
  const occ =
    occurrencesForYear(year, region).find((o) => o.slug === slug) ??
    occurrencesForYear(year + 1, region).find((o) => o.slug === slug) ??
    occurrencesForYear(year - 1, region).find((o) => o.slug === slug);
  return occ?.kind ?? null;
}

/**
 * Special days/weeks (awareness, club theme, local, Create week/day) cut over
 * from the calendar start date. Public/regional holidays stay visible in the
 * lead window so the pool-closed plan is not hidden overnight.
 */
export function observanceUsesOvernightCutover(
  slug: string,
  kind?: string | null,
  year?: number,
): boolean {
  if (isNzSchoolHolidaySlug(slug)) return false;
  if (isHolidayObservanceKind(kind)) return false;
  if (isSpecialObservanceKind(kind) || isCampaignTemplateSlug(slug)) return true;
  if (kind) return false;
  const resolvedYear = year && Number.isFinite(year) ? year : undefined;
  if (resolvedYear == null) return true;
  const catalogKind = catalogKindForSlug(slug, resolvedYear);
  if (isHolidayObservanceKind(catalogKind)) return false;
  return isSpecialObservanceKind(catalogKind) || catalogKind == null;
}

export function occurrenceForTemplate(
  slug: CampaignTemplateSlug,
  year: number,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): CalendarOccurrence | undefined {
  return occurrencesForYear(year, region).find((o) => o.slug === slug);
}

/** Templates whose last day is still today or later. Finished ones drop off. */
export function listOpenCampaignTemplates(
  year: number,
  today: string,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): CampaignTemplate[] {
  return listCampaignTemplates(year, region).filter(
    (template) => template.occursOn != null && !eventHasPassed(template.occursOn, template.endsOn, today),
  );
}

export function listCampaignTemplates(
  year: number,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): CampaignTemplate[] {
  return CAMPAIGN_TEMPLATE_SLUGS.map((slug) => {
    const occ = occurrenceForTemplate(slug, year, region);
    return {
      slug,
      shortLabel: CAMPAIGN_TEMPLATE_LABELS[slug],
      title: occ?.title ?? CAMPAIGN_TEMPLATE_LABELS[slug],
      occursOn: occ?.occursOn ?? null,
      endsOn: occ?.endsOn ?? null,
      span: campaignTemplateSpan(occ?.occursOn ?? null, occ?.endsOn ?? null),
      description: occ?.description ?? '',
      sessionIdea: occ?.sessionIdea ?? '',
      coachReminder: occ?.coachReminder ?? '',
      parentMessage: occ?.parentMessage ?? '',
      dressCode: occ?.dressCode ?? null,
      themeColor: occ?.themeColor ?? '#0d4a8a',
    };
  });
}

export function campaignTemplateBySlug(
  slug: string,
  year: number,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
): CampaignTemplate | null {
  if (!isCampaignTemplateSlug(slug)) return null;
  return listCampaignTemplates(year, region).find((t) => t.slug === slug) ?? null;
}

/**
 * Home / deck / notices / banner hide a special day/week until the NZ overnight
 * cutover of its calendar `occurs_on`, or a staging preview is on.
 * Preview is exclusive for Create week/day slugs: MHAW On hides Te Wiki.
 * Holidays stay visible in the lead window.
 */
export function isCampaignTemplateLive(
  slug: string,
  today: string,
  occursOn: string | null | undefined,
  endsOn: string | null | undefined,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
  kind?: string | null,
): boolean {
  const year = parseInt(today.slice(0, 4), 10);
  if (previewSlug && isCampaignTemplateSlug(slug)) return previewSlug === slug;
  if (!observanceUsesOvernightCutover(slug, kind, year)) return true;
  return campaignHasCutOver(occursOn, endsOn, today, nzHour);
}

/** Match a notice / pulse title back to a Create week/day campaign. */
export function campaignTemplateSlugFromText(text: string | null | undefined): CampaignTemplateSlug | null {
  const t = (text ?? '').toLowerCase();
  if (!t.trim()) return null;
  if (t.includes('te wiki') || t.includes('te reo māori') || t.includes('te reo maori')) {
    return 'te-wiki-o-te-reo-maori';
  }
  if (
    t.includes('mental health awareness') ||
    t.includes('belonging matters') ||
    t.includes('ko au ko koe') ||
    /(^|[^a-z])mhaw([^a-z]|$)/.test(t)
  ) {
    return 'mental-health-awareness-week';
  }
  if (t.includes('pink shirt')) return 'pink-shirt-day';
  return null;
}

export function isCampaignCopyLive(
  input: {
    slug?: string | null;
    kind?: string | null;
    title?: string | null;
    body?: string | null;
    occursOn?: string | null;
    endsOn?: string | null;
  },
  today: string,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
): boolean {
  let slug: string | null = null;
  if (input.slug != null && input.slug !== '') {
    slug = input.slug;
  } else {
    slug =
      campaignTemplateSlugFromText(input.title) ?? campaignTemplateSlugFromText(input.body);
  }
  if (!slug) return true;
  const year = parseInt(today.slice(0, 4), 10);
  const occ = Number.isFinite(year) && isCampaignTemplateSlug(slug)
    ? occurrenceForTemplate(slug, year)
    : Number.isFinite(year)
      ? occurrencesForYear(year, 'northland').find((o) => o.slug === slug)
      : undefined;
  return isCampaignTemplateLive(
    slug,
    today,
    input.occursOn ?? occ?.occursOn,
    input.endsOn ?? occ?.endsOn,
    previewSlug,
    nzHour,
    input.kind ?? occ?.kind,
  );
}

export function filterLiveCampaigns<
  T extends { slug: string; occursOn: string; endsOn?: string | null; kind?: string | null },
>(
  days: T[],
  today: string,
  previewSlug?: string | null,
  nzHour: number = nzHourOfDay(),
): T[] {
  return days.filter((d) =>
    isCampaignTemplateLive(d.slug, today, d.occursOn, d.endsOn, previewSlug, nzHour, d.kind),
  );
}

/** Deck/home lists: hide other campaigns, and include the previewed one even if it is outside the 14-day window. */
export function withActiveCampaignObservances(
  days: DeckObservance[],
  today: string,
  previewSlug?: string | null,
  region: 'nz' | 'northland' | 'whangarei' = 'northland',
  nzHour: number = nzHourOfDay(),
): DeckObservance[] {
  const live = filterLiveCampaigns(days, today, previewSlug, nzHour);
  if (!previewSlug || !isCampaignTemplateSlug(previewSlug)) return live;
  if (live.some((d) => d.slug === previewSlug)) return live;
  const year = parseInt(today.slice(0, 4), 10);
  if (!Number.isFinite(year)) return live;
  const occ =
    occurrencesForYear(year, region).find((o) => o.slug === previewSlug) ??
    occurrencesForYear(year + 1, region).find((o) => o.slug === previewSlug);
  return occ ? [...live, occurrenceToDeckObservance(occ)] : live;
}
